/**
 * ParentLeaveCard — apply for leave + see the status timeline for one child.
 *
 * Embedded inside the ParentHome child-tab pane. Reads/posts:
 *   GET  /api/attendance/leave?status=...
 *   POST /api/attendance/leave
 */
import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";

const today = () => new Date().toISOString().slice(0, 10);

export function ParentLeaveCard({ studentId, studentName }) {
  const [draft, setDraft] = useState({ from_date: today(), to_date: today(), reason: "" });
  const [history, setHistory] = useState([]);
  const [busy, setBusy] = useState(false);

  const load = async () => {
    try {
      const r = await api.get("/attendance/leave");
      setHistory((r.data || []).filter((x) => x.student_id === studentId));
    } catch (e) {
      // silent
    }
  };
  useEffect(() => { load(); /* eslint-disable-next-line react-hooks/exhaustive-deps */ }, [studentId]);

  const submit = async () => {
    if (!draft.reason.trim()) { toast.error("Please add a reason"); return; }
    setBusy(true);
    try {
      await api.post("/attendance/leave", { student_id: studentId, ...draft });
      toast.success("Leave application submitted");
      setDraft({ from_date: today(), to_date: today(), reason: "" });
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || e.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="vs-card p-5 lg:col-span-2" data-testid={`parent-leave-${studentId}`}>
      <div className="eyebrow mb-2">Leave application — {studentName}</div>
      <div className="grid grid-cols-1 md:grid-cols-4 gap-2 mb-3">
        <div><Label>From</Label><Input data-testid={`leave-from-${studentId}`} type="date" value={draft.from_date} onChange={(e) => setDraft({ ...draft, from_date: e.target.value })} className="bg-white" /></div>
        <div><Label>To</Label><Input data-testid={`leave-to-${studentId}`} type="date" value={draft.to_date} onChange={(e) => setDraft({ ...draft, to_date: e.target.value })} className="bg-white" /></div>
        <div className="md:col-span-2"><Label>Reason</Label><Input data-testid={`leave-reason-${studentId}`} value={draft.reason} onChange={(e) => setDraft({ ...draft, reason: e.target.value })} className="bg-white" placeholder="Family function / unwell / etc." /></div>
      </div>
      <Button data-testid={`leave-submit-${studentId}`} onClick={submit} disabled={busy}>Apply</Button>
      {history.length > 0 && (
        <div className="mt-4 space-y-1.5">
          <div className="eyebrow text-[10px]">History</div>
          {history.map((h) => (
            <div key={h.id} className="text-xs flex items-center justify-between p-2 rounded bg-[var(--vs-bg-subtle)]">
              <span><span className="font-mono">{h.from_date} → {h.to_date}</span> · {h.reason}</span>
              <span className={`capitalize px-2 py-0.5 rounded ${h.status === "pending" ? "bg-amber-100 text-amber-700" : h.status === "approved" ? "bg-emerald-100 text-emerald-700" : "bg-red-100 text-red-700"}`}>{h.status}</span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
